import Instrument from './Instrument.js';
import Notes from 'midi/Notes.js';
import Tone from 'tone';
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {SYNTH} from 'constants/instrument-types.js';
import FilterEnvelope from './FilterEnvelope.js';
import LFO from 'modulators/LFO.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';

const PORTAMENTO_FACTOR = 20.0;

/**
 * First synth made containing only 1 oscillator
 * Coded pretty shittily, before I had the concept of project wide 
 * LFOs. Hence, there are LFOs within the instrument
 */
class Synth extends Instrument {
    constructor(sequencerEngine, mixSession, context) {
        super();
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;
        this.mixSession = mixSession;
        this.context = context;

        // instantiate the Tone.PolySynth (the synth module we use here)
        this.synth = new Tone.PolySynth(1, Tone.MonoSynth);
        this.synth.volume.value = -4;

        this.lfos = [this.createLFO(), this.createLFO(), this.createLFO(), this.createLFO()];

        this.filterEnvelope = new FilterEnvelope(
            this.synth, this.store, mixSession, context);

        // keeps track whether we've set the polyphony parameter
        this.polyphonySet = false;

        // keeps track of all parameters of the synth
        this.parameters = [];

        // keeps track of which parameter goes to what signal
        this.parameterToSignal = {};
        this.parameterToLFOs = {};

        this.setupParameters();

        // todo: have this called when track starts
        this.lfos.forEach(lfo => lfo.start());

        this.refreshParameters();

        this.node = undefined;
    }

    getName() {
        return 'Synth';
    }

    isMonophonic() {
        return this.polyphony === 1;
    }

    getInstrument() {
        return this;
    }

    getInstrumentType() {
        return SYNTH;
    }

    hasLFO() {
        return true;
    }

    start() {
        this.lfos.forEach(lfo => lfo.start());
    }

    stop() {
        this.lfos.forEach(lfo => lfo.stop());
    }


    // getters and setters for automatable synth parameters
    get oscillatorType() {
        return this.oscillatorTypeParameter.get();
    }

    set oscillatorType(type) {
        let originalType = this.oscillatorTypeParameter.get();
        this.oscillatorTypeParameter.set(type);
        if (type === 'pulse' && originalType !== type) {
            this.onChangeToPulse();
        }
    }

    get oscillatorFrequency() {
        return this.oscillatorFrequencyParameter.get();
    }

    set oscillatorFrequency(x) {
        this.oscillatorFrequencyParameter.set(x);
    }

    get filterCutoff() {
        return this.filterCutoffParameter.get();
    }

    set filterCutoff(cutoff) {
        this.filterCutoffParameter.set(cutoff);
    }

    get filterQ() {
        return this.filterQParameter.get();
    }

    set filterQ(q) {
        this.filterQParameter.set(q);
    }

    get portamento() {
        return this.portamentoParameter.get();
    }

    set portamento(p) {
        this.portamentoParameter.set(p);
    }

    get polyphony() {
        if (this.polyphonyParameter === undefined) {
            return 8;
        } else {
            return this.polyphonyParameter.get();
        }
    }

    set polyphony(p) {
        this.polyphonyParameter.set(p);
    }

    get detune() {
        return this.detuneParameter.get();
    }

    set detune(x) {
        this.detuneParameter.set(x);
    }

    get pulseWidth() {
        return this.pulseWidthParameter.get();
    }

    set pulseWidth(x) {
        this.pulseWidthParameter.set(x);
    }

    get ampAttack() {
        return this.ampAttackParameter.get();
    }

    set ampAttack(a) {
        return this.ampAttackParameter.set(a);
    }

    get ampDecay() {
        return this.ampDecayParameter.get();
    }

    set ampDecay(d) {
        return this.ampDecayParameter.set(d);
    }

    get ampSustain() {
        return this.ampSustainParameter.get();
    }

    set ampSustain(s) {
        return this.ampSustainParameter.set(s);
    }

    get ampRelease() {
        return this.ampReleaseParameter.get();
    }

    set ampRelease(r) {
        return this.ampReleaseParameter.set(r);
    }

    // refreshes the synth's parameters to their saved state
    refreshParameters() {
        this.pulseWidth = this.pulseWidth;
        this.oscillatorType = this.oscillatorType;
        this.filterQ = this.filterQ;
        this.portamento = this.portamento;
        this.filterCutoff = this.filterCutoff;
    }

    createNewSynth(polyphony) {
        let node = this.node;
        this.disconnect(node);
        this.synth = new Tone.PolySynth(polyphony, Tone.MonoSynth);
        this.filterEnvelope.onNewSynth(this.synth);
        this.connect(node);
        this.polyphonySet = false;

        // need to reset all the new synth's parameters to their saved settings
        this.refreshParameters();

        this.reconnectAllLFOParameters();
    }

    triggerAttack(transpose, time, velocity) {
        let note = this.getNoteFromTranspose(transpose);
        this.synth.triggerAttack(note, time, velocity);
    }

    triggerAttackRelease(transpose, dur, time, velocity) {
        let note = this.getNoteFromTranspose(transpose);
        this.synth.triggerAttackRelease(note, dur, time, velocity);
    }

    triggerRelease(transposes) {
        let notes = transposes
            .map(x => this.getNoteFromTranspose(x));
        this.synth.triggerRelease(notes);
    }

    createLFO() {
        return new LFO(this.sequencerEngine, this.mixSession, this.context);
    }

    setupParameters(polyphony) {
        // type of oscillator: square, sine, etc
        this.oscillatorTypeParameter = new AbstractParameter(
            'Oscillator Type', this,
            (o, x) => o.synth.set({
                "oscillator": {
                    "type": x,
                },
            }),
            {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 'triangle',
                automatable: false
            });

        this.registerParameter(this.oscillatorTypeParameter);

        this.oscillatorFrequencyParameter = new AbstractParameter(
            'Oscillator Frequency', this,
            (o, x) => [],
            {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0,
                automatable: false
            });

        this.registerParameter(this.oscillatorFrequencyParameter);

        // pulse width (only applies to oscillator type: pulse)
        // Can be synced to LFO
        this.pulseWidthParameter = new AbstractParameter(
            'Pulse Width',
            this, (o, x) => o.synth.set({
                "oscillator": {
                    "width": x
                },
            }),
            {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0.1
            });

        this.registerParameter(
            this.pulseWidthParameter, (voice) => {
                return voice.oscillator.width;
            });

        // Filter cut off - can be synced to LFO
        this.filterCutoffParameter = new IntegerParameter(
            'Filter Cutoff',
            this, (o, x) => {
                o.synth.set({
                "filter": {
                    "frequency": x
                },
            });
                            },
            {
                min: 0,
                max: 10000,
                unit: 'HZ',
                defaultValue: 1200
            });

        this.registerParameter(
            this.filterCutoffParameter, (voice) => voice.filter.frequency);

        // Filter's Q - can be synced to LFO
        this.filterQParameter = new IntegerParameter(
            'Filter Q',
            this, (o, x) => o.synth.set({
                "filter": {
                    "Q": x
                },
            }),
            {
                min: 0,
                max: 9,
                unit: '',
                defaultValue: 1
            });

        this.registerParameter(
            this.filterQParameter, (voice) => voice.filter.Q);

        // "Glide" or "Portamento" of synth
        this.portamentoParameter= new AbstractParameter(
            'Portamento',
            this, (o, x) => o.synth.set({
                "portamento": x / PORTAMENTO_FACTOR
            }),
            {
                min: 0,
                max: 10,
                unit: '',
                defaultValue: 0
            });

        this.registerParameter(this.portamentoParameter);

        // Detune of oscillator (in cents)
        this.detuneParameter = new IntegerParameter(
            'Detune',
            this, (o, x) => {
                o.synth.set({
                    "detune": x
                });
            },
            {
                min: -100,
                max: 100,
                unit: '',
                defaultValue: 0,
                automatable: false
            });
        
        this.parameterToSignal[this.detuneParameter.name] =
            (voice) => voice.oscillator.detune;

        this.registerParameter(this.detuneParameter);

        // Polyphony, i.e. how many voices in the synth
        // 1 means monophonic synth
        this.polyphonyParameter = new IntegerParameter(
            'Polyphony',
            this, (o, x) => {
                if (this.polyphonySet) {
                    // avoid an infinite loop of repeatedly creating synths
                    // at the start. 
                    this.createNewSynth(x);
                } 
                this.polyphonySet = true;
            },
            {
                min: 0,
                max: 16,
                unit: '',
                defaultValue: 1,
                automatable: false
            });

        this.registerParameter(this.polyphonyParameter);

        this.ampAttackParameter = new AbstractParameter(
            'Amp Attack', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 10
            });
        this.registerParameter(this.ampAttackParameter);
        this.ampDecayParameter = new AbstractParameter(
            'Amp Decay', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 10
            });
        this.registerParameter(this.ampDecayParameter);
        this.ampSustainParameter = new AbstractParameter(
            'Amp Sustain', this, (o, x) => x, {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 1
            });
        this.registerParameter(this.ampSustainParameter);
        this.ampReleaseParameter = new AbstractParameter(
            'Amp Release', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 10
            });
        this.registerParameter(this.ampReleaseParameter);
    }

    registerParameter(parameter, signalGetter) {
        if (signalGetter !== undefined) {
            this.parameterToSignal[parameter.name] = signalGetter;
        }
        parameter.setStore(this.store);
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        parameter.setTrack(this.context.track);
        this.parameters.push(parameter);
    }

    getCopy(context) {
        let synth = new Synth(this.store, this.mixSession, context);
        synth.instrumentType = this.instrumentType;
        synth.oscillatorFrequency = this.oscillatorFrequency;
        synth.filterCutoff = this.filterCutoff;
        synth.filterQ = this.filterQ;
        synth.polyphony = this.polyphony;
        synth.portamento = this.portamento;
        synth.filterEnvelope.copyFrom(this.filterEnvelope);
        synth.oscillatorType = this.oscillatorType;
        synth.pulseWidth = this.pulseWidth;
        
        // copy over the lfo settings
        for (let i in synth.lfos) {
            synth.lfos[i].copyFrom(this.lfos[i]);
        }

        // connect all the parameters to their respective lfos
        // in the new synth
        synth.parameterToLFOs = {};
        for (let parameterName in this.parameterToLFOs) {
            let lfos = this.parameterToLFOs[parameterName];
            let parameter = synth.getParameterByName(parameterName);
            lfos.forEach(lfo => {
                let lfoToConnect = synth.lfos[this.lfos.indexOf(lfo)];
                synth.connectLFOToParameter(lfoToConnect, parameter);
            });
        }
 
        return synth;
    }

    getParameters() {
        return [... this.parameters,
                ... this.filterEnvelope.parameters
               ];
    }

    getJson() {
        let json = {
            instrumentType: this.getInstrumentType(),
            oscillatorType: this.oscillatorType,
            oscillatorFrequency: this.oscillatorFrequency,
            filterCutoff: this.filterCutoff,
            filterQ: this.filterQ,
            polyphony: this.polyphony,
            portamento: this.portamento,
            pulseWidth: this.pulseWidth,
            lfos: this.lfos.map(lfo => lfo.getJson()),
            parameterToLFOs: this.getParameterToLFOsJson(),
            filterEnvelope: this.filterEnvelope.getJson(),
            ampAttack: this.ampAttack,
            ampDecay: this.ampDecay,
            ampSustain: this.ampSustain,
            ampRelease: this.ampRelease,
        };

        json = saveParameterToJson(json, 'filterCutoffParameter', this.filterCutoffParameter);
        json = saveParameterToJson(json, 'filterQParameter', this.filterQParameter);
        json = saveParameterToJson(json, 'portamentoParameter', this.portamentoParameter);
        json = saveParameterToJson(json, 'pulseWidthParameter', this.pulseWidthParameter);
        json = saveParameterToJson(json, 'oscillatorFrequencyParameter', this.oscillatorFrequencyParameter);
        json = saveParameterToJson(json, 'oscillatorTypeParameter', this.oscillatorTypeParameter);
        json = saveParameterToJson(json, 'ampAttackParameter', this.ampAttackParameter);
        json = saveParameterToJson(json, 'ampDecayParameter', this.ampDecayParameter);
        json = saveParameterToJson(json, 'ampSustainParameter', this.ampSustainParameter);
        json = saveParameterToJson(json, 'ampReleaseParameter', this.ampReleaseParameter);
        
        return json;
    }

    getParameterToLFOsJson() {
        let json ={};
        for (let parameter in this.parameterToLFOs) {
            let lfos = [];
            this.parameterToLFOs[parameter].forEach(
                lfo => lfos.push(this.lfos.indexOf(lfo)));

            json[parameter] = lfos;
        }

        return json;
    }

    fromJson(json) {
        if ('instrumentType' in json) {
            this.instrumentType = json.instrumentType;
        }
        if ('polyphony' in json) {
            this.polyphony = json.polyphony;
        }
        if ('oscillatorFrequency' in json) {
            this.oscillatorFrequency  = json.oscillatorFrequency;
        }
        if ('pulseWidth' in json) {
            this.pulseWidth = json.pulseWidth;
        }
        if ('oscillatorType' in json) {
            this.oscillatorType = json.oscillatorType;
        }

        if ('filterCutoff' in json) {
            this.filterCutoff = json.filterCutoff;
        }

        if ('filterQ' in json) {
            this.filterQ = json.filterQ;
        }

        if ('portamento' in json) {
            this.portamento = json.portamento;
        }

        if ('filterEnvelope' in json) {
            this.filterEnvelope.fromJson(json.filterEnvelope);
        }

        if ('lfos' in json) {
            for (let i in json.lfos) {
                this.lfos[i].fromJson(json.lfos[i]);
            }
        }

        if ('parameterToLFOs' in json) {
            this.loadParameterToLFOsJson(json.parameterToLFOs);
        }

        if ('ampAttack' in json) {
            this.ampAttack = json.ampAttack;
        }

        if ('ampDecay' in json) {
            this.ampDecay= json.ampDecay;
        }

        if ('ampSustain' in json) {
            this.ampSustain = json.ampSustain;
        }

        if ('ampRelease' in json) {
            this.ampRelease = json.ampRelease;
        }

        loadParameterFromJson(
            json, 'filterCutoffParameter', this.filterCutoffParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'filterQParameter', this.filterQParameter, this.sequencerEngine);
            
        loadParameterFromJson(
            json, 'portamentoParameter', this.portamentoParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'pulseWidthParameter', this.pulseWidthParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'oscillatorFrequencyParameter', this.oscillatorFrequencyParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'oscillatorTypeParameter', this.oscillatorTypeParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'ampAttackParameter', this.ampAttackParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'ampDecayParameter', this.ampDecayParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'ampSustainParameter', this.ampSustainParameter, this.sequencerEngine);
        loadParameterFromJson(
            json, 'ampReleaseParameter', this.ampReleaseParameter, this.sequencerEngine);
    }

    getParameterByName(parameterName) {
        let parameter = this.parameters.filter(p => p.name === parameterName);
        if (parameter.length < 1) {
            return undefined;
        }

        return parameter[0];
    }

    loadParameterToLFOsJson(json) {
        for (let parameterName in json) {
            let parameter = this.getParameterByName(parameterName);
            if (parameter === undefined) {
                continue;
            }
            
            json[parameterName].forEach(
                lfoNumber => this.connectLFOToParameter(
                    this.lfos[lfoNumber],
                    parameter));
        }
    }

    connectLFOToParameter(lfo, parameter) {
        let didNotConnect = false;
        if (this.parameterToSignal[parameter.name] === undefined) {
            return;
        }
        this.synth.voices.forEach(
            (voice, i) => {
                let signal = this.parameterToSignal[parameter.name](voice);
                if (signal === undefined) {
                    didNotConnect = true;
                } else {
                    lfo.connect(signal, parameter, i);
                }
            });

        if (didNotConnect) {
            return;
        }
        if (parameter.name in this.parameterToLFOs) {
            this.parameterToLFOs[parameter.name].add(lfo);
        } else {
            this.parameterToLFOs[parameter.name] = new Set([lfo]);
        }

        this.refreshParameters();
    }

    disconnectLFOToParameter(lfo, parameter) {
        if (this.parameterToLFOs[parameter.name].has(lfo)) {
            this.synth.voices.forEach(
                (voice,i) => {
                    lfo.disconnect(this.parameterToSignal[parameter.name](voice), parameter, i);
                });
            this.parameterToLFOs[parameter.name].delete(lfo);
        }
    }

    isParameterConnectedToLFO(parameter, lfo) {
        return parameter.name in this.parameterToLFOs &&
            this.parameterToLFOs[parameter.name].has(lfo);
    }

    toggleParameterWithLFO(parameter, lfo) {
        if (this.isParameterConnectedToLFO(parameter, lfo)) {
            this.disconnectLFOToParameter(lfo, parameter);
        } else{
            this.connectLFOToParameter(lfo, parameter);
        }
    }

    reconnectAllLFOParameters() {
        for (let parameterName in this.parameterToLFOs) {
            let lfos = this.parameterToLFOs[parameterName];
            let parameter = this.getParameterByName(parameterName);
            lfos.forEach(lfo => this.connectLFOToParameter(lfo, parameter));
        }
    }

    // when we change to pulse, we should reconnect the pulseWidthParameter
    onChangeToPulse() {
        if (this.pulseWidthParameter.name in this.parameterToLFOs) {
            this.parameterToLFOs[this.pulseWidthParameter.name].forEach(
                lfo =>
                    this.connectLFOToParameter(lfo, this.pulseWidthParameter, true));
        }
    }

    // connects the synth to the given audio node (i.e. the mix)
    connect(node) {
        if (node !== undefined) {
            this.synth.connect(node);
            this.node = node;
        }
    }

    // connects the synth from the given audio node (i.e. the mix)
    disconnect(node) {
        this.synth.disconnect(node);
        this.node = undefined;
    }

    // returns the amplitude envelopes of the synth
    // used when setting attack and release for a trigger
    getEnvelopes() {
        return this.synth.voices.map(x => x.envelope);
    }

    releaseAll() {
        this.lastNote = undefined;
        this.synth.releaseAll();
    }

    getNoteFromTranspose(transpose) {
        transpose += this.sequencerEngine.transpose;
        return this.getDefaultNote().transpose(transpose + this.oscillatorFrequency);
    }

    setEnvelopes(stepData) {
        let envelopes = this.getEnvelopes();

        // set the attack and release of this trigger
        envelopes.forEach(
            envelope =>
                envelope.attack = new Tone.Time((1 + this.ampAttack) / 1000.0));
        envelopes.forEach(
            envelope =>
                envelope.release = new Tone.Time((1 + this.ampRelease) / 1000.0));
        envelopes.forEach(
            envelope =>
                envelope.sustain = Math.min(0.99, this.ampSustain));
        envelopes.forEach(
            envelope =>
                envelope.decay = new Tone.Time((1 + this.ampDecay) / 1000.0));
    }

    releaseAll() {
        this.synth.voices.forEach(voice => voice.triggerRelease());
    }
}

export default Synth;
